import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import cx from 'classnames';
import type {
  FC,
  FunctionComponentElement,
  ReactElement,
  ReactNode,
} from 'react';
import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SwiperOptions } from 'swiper';
import { Autoplay, Pagination } from 'swiper';
import { Swiper } from 'swiper/react';
import type { ScrollFrameItemProps } from './ScrollFrameItem';
import './index.less';

export interface ScrollFrameProps extends SwiperOptions {
  /** Type of layout, options:`horizontal`, `vertical` */
  mode?: 'horizontal' | 'vertical';
  children: ReactNode;
  /** preview number, default 4 */
  slidesPerView?: number;
  /** custom button render */
  button?: { buttonPrev: ReactElement; buttonNext: ReactElement } | null;
  /** frame's width, default: 350 */
  width?: number;
  /** frame's height, default: 72 */
  height?: number;
  /** Type of boolean, can you touch move */
  allowTouchMove?: boolean;
  paginationBottom?: number;
  /** other props, you can see https://swiperjs.com/swiper-api#parameters */
  [key: string]: any;
}

const ScrollFrame: FC<ScrollFrameProps> = (p) => {
  const prevRef = useRef() as any;
  const nextRef = useRef() as any;
  const swiperRef = useRef() as any;
  const {
    children,
    mode = 'horizontal',
    slidesPerView = 4,
    button,
    width = mode === 'vertical' ? 72 : 350,
    height = mode === 'vertical' ? 350 : 72,
    allowTouchMove = false,
    paginationBottom,
    ...otherProps
  } = p;
  const [swiperSize, setSwiperSize] = useState<number>(0);

  const handlePrev = () => {
    swiperRef.current.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current.slideNext();
  };

  const classPrefix =
    mode === 'horizontal'
      ? 'beukay-scroll-frame-horizontal'
      : 'beukay-scroll-frame';

  // swiper style 赋值
  const swiperStyle =
    mode === 'horizontal'
      ? { width: `${swiperSize}px`, paddingBottom: `${paginationBottom}px` }
      : { height: `${swiperSize}px` };

  const wrapStyle = { width: `${width}px`, height: `${height}px` };

  const renderItem = useMemo(() => {
    return Children.map(children, (child) => {
      const childElement =
        child as FunctionComponentElement<ScrollFrameItemProps>;
      if (
        isValidElement(child) &&
        childElement.type.displayName === 'SwiperSlide'
      ) {
        return child;
      } else {
        console.error(
          'warning: the ScrollFrame has a child not ScrollFrame.Item',
        );
      }
    });
  }, [children]);

  const buttonPrev =
    button === null
      ? ''
      : button?.buttonPrev ?? (
          <LeftOutlined
            ref={prevRef}
            className={`${classPrefix}-button-default`}
            onClick={handlePrev}
          />
        );

  const buttonNext =
    button === null
      ? ''
      : button?.buttonNext ?? (
          <RightOutlined
            ref={nextRef}
            className={`${classPrefix}-button-default`}
            onClick={handleNext}
          />
        );

  useEffect(() => {
    const prevButtonWidth = prevRef.current?.clientWidth || 0;
    const prevButtonHeight = prevRef.current?.clientHeight || 0;
    const nextButtonWidth = nextRef.current?.clientWidth || 0;
    const nextButtonHeight = nextRef.current?.clientHeight || 0;

    const buttonTotalWidth = prevButtonWidth + nextButtonWidth;
    const buttonTotalHeight = prevButtonHeight + nextButtonHeight;

    const size = mode === 'horizontal' ? width : height;
    // 计算button占据位置大小
    const button_size =
      mode === 'horizontal' ? buttonTotalWidth : buttonTotalHeight;
    // 计算swiper最后大小
    const swiper_size = size - button_size;

    setSwiperSize(swiper_size);
  }, [children]);

  useEffect(() => {
    if (swiperRef?.current && otherProps?.autoplay) {
      swiperRef.current?.autoplay.start();
    }
  }, [swiperRef?.current]);
  return (
    <div className={classPrefix} style={wrapStyle}>
      {buttonPrev &&
        cloneElement(buttonPrev, {
          ref: prevRef,
          className: cx(
            buttonPrev.props.className,
            `${classPrefix}-button-prev`,
          ),
          onClick: handlePrev,
        })}
      {buttonNext &&
        cloneElement(buttonNext, {
          ref: nextRef,
          className: cx(
            buttonNext.props.className,
            `${classPrefix}-button-next`,
          ),
          onClick: handleNext,
        })}
      <Swiper
        // className={`${classPrefix}-swiper`}
        style={swiperStyle}
        modules={[Autoplay, Pagination]}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView={slidesPerView}
        direction={mode}
        allowTouchMove={allowTouchMove}
        {...otherProps}
      >
        {renderItem}
      </Swiper>
    </div>
  );
};

export default ScrollFrame;
